import { useState, useEffect, useRef } from 'react';
import { createRootRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth.js';
import { useTheme } from '../hooks/useTheme.js';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/layout/Sidebar';

/**
 * @graph-node {Page}
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business ルートレイアウト。認証チェック・Header・Sidebar・背景デコレーションを統合し全ページの共通レイアウトを提供
 * @graph-connects useAuth [calls] 認証フック呼び出し
 * @graph-connects Header [calls] ヘッダーコンポーネント呼び出し
 * @graph-connects Sidebar [calls] サイドバーコンポーネント呼び出し
 * @graph-connects LoadingScreen [calls] ローディング画面コンポーネント呼び出し
 */
export const Route = createRootRoute({
  component: RootLayout,
});

/** @graph-connects none */
function RootLayout() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // テーマ初期化
  useTheme();

  const isLoginPage = location.pathname === '/login';

  // location.pathname / navigatedRef ガード: 認証 redirect useEffect 内で navigate を
  // 連続発火させない。location.pathname を deps に含めると route transition 中に
  // 多重 render で effect が再発火し、auth re-fetch 等で isLoading / isAuthenticated
  // が変動するシナリオも合わさって `history.notify` → `router.load` →
  // `setIsTransitioning` の同期連発で Minified React error #185 を起こす
  // （docs/web/product-innovation-lab.md「1 回ガード ref」規則）。
  // __root は SPA 起動中ずっと mount されるため、auth 回復時に navigatedRef を
  // リセットして次回 auth 失敗時にも redirect が走るようにする。
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const navigatedRef = useRef(false);

  // 認証リダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      navigatedRef.current = false;
      return;
    }
    if (navigatedRef.current) return;
    if (isLoginPage || isLoading) return;
    navigatedRef.current = true;
    void navigate({ to: '/login', search: { redirect: pathnameRef.current } });
  }, [isAuthenticated, isLoading, isLoginPage, navigate]);

  // ログインページ
  if (isLoginPage) {
    return <Outlet />;
  }

  // ローディング中 or 未認証（リダイレクト中）
  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)]">
        <div className="gradient-orb gradient-orb-1" />
        <div className="gradient-orb gradient-orb-2" />
        <div className="h-14 glass-heavy" />
        <div className="flex min-h-[calc(100vh-3.5rem)]">
          <div className="hidden lg:block w-56" />
          <main className="flex-1 p-4 md:p-6 lg:p-8">
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="h-8 w-48 rounded bg-[var(--color-bg-hover)] skeleton-shimmer" />
              <div className="h-4 w-80 rounded bg-[var(--color-bg-hover)] skeleton-shimmer" />
              <div className="h-40 rounded-xl bg-[var(--color-bg-hover)] skeleton-shimmer" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]">
      {/* 背景デコレーション */}
      <div className="gradient-orb gradient-orb-1" />
      <div className="gradient-orb gradient-orb-2" />

      <Header
        user={user}
        onLogout={logout}
        onMenuToggle={() => {
          setIsSidebarOpen(true);
        }}
      />
      <div className="flex min-h-[calc(100vh-3.5rem)]">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => {
            setIsSidebarOpen(false);
          }}
        />
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 lg:p-8 animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
